import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Trophy, Clock, Zap, Lock, CheckCircle, Shield, TrendingUp, DollarSign, Target, AlertTriangle, XCircle, Sparkles, Brain, Lightbulb, BarChart3, ChevronDown, History } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// Beautiful Feedback Renderer Component
const FeedbackRenderer = ({ feedback, aiScore }) => {
  if (!feedback) return null;

  // Check if this is AI feedback (contains "AI EVALUATION")
  const isAIFeedback = feedback.includes('AI EVALUATION') || feedback.includes('Powered by Claude');

  if (isAIFeedback) {
    return <AIFeedbackRenderer feedback={feedback} aiScore={aiScore} />;
  }

  return <RuleBasedFeedbackRenderer feedback={feedback} />;
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, color, children, defaultOpen = false, count }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const colorClasses = {
    green: 'bg-green-900/30 border-green-700 text-green-300',
    orange: 'bg-orange-900/30 border-orange-700 text-orange-300',
    blue: 'bg-blue-900/30 border-blue-700 text-blue-300',
    purple: 'bg-purple-900/30 border-purple-700 text-purple-300',
    gray: 'bg-slate-700/50 border-slate-600 text-slate-300',
  };

  return (
    <div className={`rounded-lg border ${colorClasses[color]}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-3 flex items-center justify-between hover:bg-black/5 transition-colors"
      >
        <div className="flex items-center gap-2 font-semibold">
          <Icon className="w-4 h-4" />
          {title}
          {count > 0 && <span className="text-xs bg-slate-600/50 px-2 py-0.5 rounded-full">{count}</span>}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

// Collapsible Challenge Section Component (for Scenario/Requirements)
const CollapsibleChallengeSection = ({ title, icon: Icon, bgColor, borderColor, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`${bgColor} border-l-4 ${borderColor} mb-4 rounded-r-lg overflow-hidden`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-black/5 transition-colors text-left"
      >
        <h3 className="font-semibold flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5" />}
          {title}
        </h3>
        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="px-4 pb-4 pt-0">{children}</div>}
    </div>
  );
};

// AI Feedback Renderer
const AIFeedbackRenderer = ({ feedback, aiScore }) => {
  // Strip the redundant header line
  const cleanedFeedback = feedback
    .replace(/^=== AI EVALUATION \(Powered by Claude\) ===\s*/i, '')
    .replace(/^📊 Scores:[\s\S]*?Overall:\s*\d+\/1000\s*/i, '') // Remove scores block too (we show them separately)
    .trim();

  // Better parsing - find section boundaries
  const extractSection = (text, sectionName) => {
    // Find the section header
    const headerPatterns = [
      new RegExp(`✅\\s*${sectionName}:?`, 'i'),
      new RegExp(`⚠️\\s*${sectionName}:?`, 'i'),
      new RegExp(`💡\\s*${sectionName}:?`, 'i'),
      new RegExp(`⚖️\\s*${sectionName}:?`, 'i'),
      new RegExp(`📖\\s*${sectionName}:?`, 'i'),
      new RegExp(`📝\\s*${sectionName}:?`, 'i'),
      new RegExp(`^${sectionName}:?\\s*$`, 'im'),
    ];

    let startIdx = -1;
    for (const pattern of headerPatterns) {
      const match = text.match(pattern);
      if (match) {
        startIdx = text.indexOf(match[0]) + match[0].length;
        break;
      }
    }

    if (startIdx === -1) return '';

    // Find the next section (look for emoji headers or ALLCAPS headers)
    const nextSectionPattern = /\n\s*(?:✅|⚠️|💡|⚖️|📖|📝)\s*[A-Za-z]/;
    const remaining = text.slice(startIdx);
    const nextMatch = remaining.match(nextSectionPattern);

    const content = nextMatch
      ? remaining.slice(0, nextMatch.index)
      : remaining;

    return content.trim();
  };

  const parseBullets = (text) => {
    if (!text) return [];
    return text.split('\n')
      .map(line => line.trim())
      .filter(line => line.startsWith('•') || line.startsWith('-') || line.startsWith('→') || line.startsWith('✓'))
      .map(line => line.replace(/^[•\-→✓]\s*/, '').trim())
      .filter(Boolean)
      .slice(0, 5); // Limit to 5 items max
  };

  // Extract summary (first paragraph before any emoji section)
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const summaryMatch = cleanedFeedback.match(/^📝\s*Summary:?\s*\n([\s\S]*?)(?=\n\s*[✅⚠️💡⚖️📖])/i)
    || cleanedFeedback.match(/^([\s\S]*?)(?=\n\s*[✅⚠️💡⚖️])/);
  const fullSummary = summaryMatch ? summaryMatch[1].trim() : '';
  const summaryNeedsTruncation = fullSummary.length > 300;
  const summary = summaryExpanded ? fullSummary : fullSummary.slice(0, 300);

  const strengths = parseBullets(extractSection(cleanedFeedback, 'Strengths'));
  const weaknesses = parseBullets(extractSection(cleanedFeedback, 'Weaknesses'));
  const suggestions = parseBullets(extractSection(cleanedFeedback, 'Suggestions'));
  const tradeOffs = parseBullets(extractSection(cleanedFeedback, 'Trade-offs'));

  // Detailed feedback - limit length
  const detailedSection = extractSection(cleanedFeedback, 'Detailed Feedback') || extractSection(cleanedFeedback, 'Detailed Analysis');
  const detailedFeedback = detailedSection.slice(0, 500) + (detailedSection.length > 500 ? '...' : '');

  return (
    <div className="space-y-3">
      {/* AI Header */}
      <div className="flex items-center gap-2 text-purple-700 bg-purple-50 rounded-lg px-4 py-2">
        <Sparkles className="w-5 h-5" />
        <span className="font-semibold">AI-Powered Evaluation</span>
        <span className="text-xs text-purple-500 ml-auto">Powered by Claude</span>
      </div>

      {/* Summary - always visible */}
      {fullSummary && (
        <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
          <p className="text-slate-300 text-sm">
            {summary}
            {summaryNeedsTruncation && !summaryExpanded && '... '}
            {summaryNeedsTruncation && (
              <button
                onClick={() => setSummaryExpanded(!summaryExpanded)}
                className="text-purple-600 hover:text-purple-800 font-medium ml-1"
              >
                {summaryExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </p>
        </div>
      )}

      {/* Collapsible sections */}
      {strengths.length > 0 && (
        <CollapsibleSection title="Strengths" icon={CheckCircle} color="green" count={strengths.length} defaultOpen={true}>
          <ul className="space-y-1">
            {strengths.map((s, i) => (
              <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {weaknesses.length > 0 && (
        <CollapsibleSection title="Weaknesses" icon={AlertTriangle} color="orange" count={weaknesses.length} defaultOpen={true}>
          <ul className="space-y-1">
            {weaknesses.map((w, i) => (
              <li key={i} className="text-sm text-orange-700 flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">!</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {suggestions.length > 0 && (
        <CollapsibleSection title="Suggestions" icon={Lightbulb} color="blue" count={suggestions.length}>
          <ul className="space-y-1">
            {suggestions.map((s, i) => (
              <li key={i} className="text-sm text-blue-700 flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">→</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {tradeOffs.length > 0 && (
        <CollapsibleSection title="Trade-offs" icon={Brain} color="purple" count={tradeOffs.length}>
          <ul className="space-y-1">
            {tradeOffs.map((t, i) => (
              <li key={i} className="text-sm text-purple-700 flex items-start gap-2">
                <span className="text-purple-500 mt-0.5">⚖</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {detailedFeedback && (
        <CollapsibleSection title="Detailed Analysis" icon={BarChart3} color="gray">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{detailedFeedback}</p>
        </CollapsibleSection>
      )}
    </div>
  );
};

// Rule-Based Feedback Renderer
const RuleBasedFeedbackRenderer = ({ feedback }) => {
  // Parse sections
  const sections = [];
  const sectionRegex = /=== ([A-Z\s]+) ===([\s\S]*?)(?====|$)/g;
  let match;

  while ((match = sectionRegex.exec(feedback)) !== null) {
    sections.push({
      title: match[1].trim(),
      content: match[2].trim()
    });
  }

  const getSectionIcon = (title) => {
    if (title.includes('ARCHITECTURE')) return <Shield className="w-4 h-4" />;
    if (title.includes('EFFICIENCY')) return <Zap className="w-4 h-4" />;
    if (title.includes('COST')) return <DollarSign className="w-4 h-4" />;
    if (title.includes('RELIABILITY')) return <Shield className="w-4 h-4" />;
    if (title.includes('FINAL')) return <Trophy className="w-4 h-4" />;
    return <Target className="w-4 h-4" />;
  };

  const getSectionColor = (title) => {
    if (title.includes('ARCHITECTURE')) return 'blue';
    if (title.includes('EFFICIENCY')) return 'yellow';
    if (title.includes('COST')) return 'green';
    if (title.includes('RELIABILITY')) return 'purple';
    if (title.includes('FINAL')) return 'indigo';
    return 'gray';
  };

  const parseLines = (content) => {
    return content.split('\n').filter(line => line.trim()).map(line => {
      const isSuccess = line.includes('✅');
      const isWarning = line.includes('⚠️');
      const isError = line.includes('❌');
      const isBonus = line.includes('🎁');
      const isCelebration = line.includes('🎉');

      return { text: line.trim(), isSuccess, isWarning, isError, isBonus, isCelebration };
    });
  };

  return (
    <div className="space-y-3">
      {sections.map((section, idx) => {
        const color = getSectionColor(section.title);
        const lines = parseLines(section.content);
        const isFinal = section.title.includes('FINAL');

        return (
          <div
            key={idx}
            className={`rounded-lg p-4 border ${
              isFinal
                ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'
                : `bg-${color}-50 border-${color}-200`
            }`}
          >
            <h4 className={`font-semibold mb-2 flex items-center gap-2 text-${color}-800`}>
              {getSectionIcon(section.title)}
              {section.title.replace('SCORE', '').replace('VALIDATION', '').trim()}
            </h4>
            <div className="space-y-1">
              {lines.map((line, lineIdx) => (
                <div
                  key={lineIdx}
                  className={`text-sm flex items-start gap-2 ${
                    line.isSuccess ? 'text-green-700' :
                    line.isWarning ? 'text-orange-700' :
                    line.isError ? 'text-red-700' :
                    line.isBonus ? 'text-purple-700' :
                    line.isCelebration ? 'text-green-700 font-semibold' :
                    'text-gray-700'
                  }`}
                >
                  <span>{line.text}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
import { challengesAPI, submissionsAPI } from '../services/api';
import useUserStore from '../store/userStore';
import DesignCanvas from '../components/DesignCanvas';
import { toast } from 'react-toastify';
import { trackChallengeStart, trackChallengeSubmit } from '../utils/analytics';

const DIFFICULTY_COLORS = {
  beginner: 'bg-green-500',
  intermediate: 'bg-orange-500',
  advanced: 'bg-red-500',
};

const ChallengePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams.get('submission');
  const { user, fetchProfile, fetchInventory, inventoryLoaded, token } = useUserStore();

  const [challenge, setChallenge] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [savedDesign, setSavedDesign] = useState(null);
  const [referenceSolution, setReferenceSolution] = useState(null);
  const [solutionLocked, setSolutionLocked] = useState(false);
  const [solutionUnlockCost, setSolutionUnlockCost] = useState(75);
  const [viewingHistoricalSubmission, setViewingHistoricalSubmission] = useState(false);

  useEffect(() => {
    loadChallenge();
  }, [id, token, submissionId]);

  // Separate effect for inventory - runs once when conditions are met
  useEffect(() => {
    if (token && !inventoryLoaded && token !== 'demo_token') {
      fetchInventory().catch(err => {
        console.error('Failed to fetch inventory:', err);
      });
    }
  }, [token, inventoryLoaded, fetchInventory]);

  const loadChallenge = async () => {
    try {
      setLoading(true);

      // Demo mode mock data
      if (token === 'demo_token') {
        const mockChallenge = {
          id: parseInt(id),
          title: 'Design a URL Shortener (TinyURL)',
          description: 'Build a service that converts long URLs into short, shareable links',
          difficulty: 'beginner',
          story_text: 'Your startup needs a URL shortening service. Users submit long URLs and receive short codes (e.g., tiny.url/abc123). The service must handle 1000 URL creations per second and 10,000 redirects per second with sub-100ms latency. Design a scalable system.',
          requirements: {
            qps_write: 1000,
            qps_read: 10000,
            latency_ms: 100,
            url_retention_years: 5,
            unique_urls: 1000000000
          },
          score_criteria: {
            efficiency: { weight: 0.4, qps_threshold: 10000, latency_threshold_ms: 100 },
            cost: { weight: 0.3, monthly_budget: 500, penalty_per_100_over: 5 },
            reliability: { weight: 0.3, required_components: ['cache', 'database'], spof_penalty: 20 }
          },
          base_reward: 100,
          estimated_time_minutes: 30,
          tags: ['caching', 'distributed-ids', 'base62', 'read-heavy']
        };

        setChallenge(mockChallenge);
        setIsUnlocked(true);
        setUserProgress({ is_completed: false, best_score: 0, attempts: 0 });
      } else {
        const response = await challengesAPI.getById(id);
        setChallenge(response.data.challenge);
        setIsUnlocked(response.data.is_unlocked);
        setUserProgress(response.data.user_progress);

        // Track challenge view in analytics
        trackChallengeStart(
          String(response.data.challenge.id),
          response.data.challenge.title,
          response.data.challenge.difficulty
        );

        // Store reference solution for "Show Solution" button
        if (response.data.reference_solution) {
          if (response.data.reference_solution.is_locked) {
            // Solution exists but is locked - user needs to pay DP
            setSolutionLocked(true);
            setSolutionUnlockCost(response.data.reference_solution.unlock_cost || 75);
            setReferenceSolution(null);
          } else if (response.data.reference_solution.solution_json) {
            // Solution is unlocked
            setSolutionLocked(false);
            setReferenceSolution(response.data.reference_solution.solution_json);
          }
        }

        // Check if we're loading a specific historical submission
        if (submissionId) {
          try {
            const submissionRes = await submissionsAPI.getById(submissionId);
            if (submissionRes.data.submission?.design_json) {
              setSavedDesign(submissionRes.data.submission.design_json);
              setViewingHistoricalSubmission(true);
              // Also show the submission result
              setLastSubmission({
                final_score: submissionRes.data.submission.final_score,
                efficiency_score: submissionRes.data.submission.efficiency_score,
                cost_score: submissionRes.data.submission.cost_score,
                reliability_score: submissionRes.data.submission.reliability_score,
                feedback: submissionRes.data.submission.feedback_text,
                grade: submissionRes.data.submission.grade,
              });
            }
          } catch (subErr) {
            console.log('Could not load specific submission:', subErr);
            // Fall back to best design
            setViewingHistoricalSubmission(false);
          }
        } else {
          // Load saved design from previous submissions (best score)
          setViewingHistoricalSubmission(false);
          try {
            const submissionsRes = await submissionsAPI.getByChallengeId(id);
            if (submissionsRes.data.best_design_json) {
              setSavedDesign(submissionsRes.data.best_design_json);
            }
          } catch (subErr) {
            // No previous submissions - that's fine
            console.log('No previous submissions found');
          }
        }

        // If no user design, start with blank canvas (Users component only)
        // Reference solution is viewable via toggle, not auto-filled
      }
    } catch (error) {
      console.error('Error loading challenge:', error);
      toast.error('Failed to load challenge');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDesign = async (designData) => {
    try {
      setSubmitting(true);

      if (token === 'demo_token') {
        // Demo mode - mock scoring (0-1000 scale)
        const numComponents = designData.component_list.length;
        const hasCache = designData.design_json.nodes.some(n => n.data.category === 'cache');
        const hasDatabase = designData.design_json.nodes.some(n => n.data.category === 'database');

        // Scores on 0-1000 scale
        const efficiencyScore = Math.min(950, 600 + numComponents * 100);
        const costScore = Math.max(700, 1000 - numComponents * 50);
        const reliabilityScore = (hasCache && hasDatabase) ? 900 : 700;
        const finalScore = Math.round(efficiencyScore * 0.4 + costScore * 0.3 + reliabilityScore * 0.3);
        const pointsEarned = finalScore; // DP equals score on 0-1000 scale

        const mockSubmission = {
          id: Date.now(),
          efficiency_score: efficiencyScore,
          cost_score: costScore,
          reliability_score: reliabilityScore,
          final_score: finalScore,
          points_earned: pointsEarned,
          passed: finalScore >= 700,
          feedback: `=== DEMO MODE SCORING ===

=== EFFICIENCY SCORE ===
✅ Components added: ${numComponents}
✅ Design has architectural components
Final Efficiency: ${efficiencyScore}/1000 (weight: 0.4)

=== COST SCORE ===
✅ Component count: ${numComponents}
${numComponents <= 5 ? '✅ Good cost efficiency' : '⚠️ Consider simplifying design'}
Final Cost: ${costScore}/1000 (weight: 0.3)

=== RELIABILITY SCORE ===
${hasCache ? '✅ Caching layer present' : '⚠️ Consider adding caching'}
${hasDatabase ? '✅ Database layer present' : '⚠️ Database required'}
${numComponents > 2 ? '✅ No single point of failure' : '⚠️ Add redundancy'}
Final Reliability: ${reliabilityScore}/1000 (weight: 0.3)

=== FINAL RESULTS ===
Overall Score: ${finalScore}/1000
Design Points Earned: ${pointsEarned} DP
${finalScore >= 700 ? '🎉 Challenge PASSED! (Score >= 700)' : '❌ Challenge incomplete (Need >= 700 to pass)'}

Note: This is demo mode. Connect to the backend API for real scoring.`
        };

        setLastSubmission(mockSubmission);

        // Update demo user DP
        if (mockSubmission.passed) {
          const currentUser = useUserStore.getState().user;
          if (currentUser) {
            const currentDP = currentUser.design_points || 0;
            useUserStore.setState({
              user: { ...currentUser, design_points: currentDP + pointsEarned }
            });
          }
          toast.success(`🎉 Challenge Passed! Earned ${pointsEarned} DP`);
        } else {
          toast.info(`Score: ${finalScore}/1000. Keep trying!`);
        }
      } else {
        // Real API mode
        const submission = {
          challenge_id: parseInt(id),
          design_json: designData.design_json,
          component_list: designData.component_list,
          submission_time_seconds: Math.floor(Date.now() / 1000),
          use_ai: designData.use_ai !== false, // Default to true
        };

        const response = await submissionsAPI.submit(submission);
        setLastSubmission(response.data.submission);

        // Track submission in analytics
        trackChallengeSubmit(
          String(id),
          challenge.title,
          challenge.difficulty,
          response.data.submission.final_score,
          response.data.submission.grade || ''
        );

        if (response.data.submission.passed) {
          toast.success(`🎉 Challenge Passed! Earned ${response.data.submission.points_earned} DP`);
        } else {
          toast.info(`Score: ${response.data.submission.final_score}/1000. Keep trying!`);
        }

        await fetchProfile();
        await loadChallenge();
      }
    } catch (error) {
      console.error('Error submitting design:', error);
      const message = error.response?.data?.error || 'Failed to submit design';
      toast.error(message);

      if (error.response?.data?.missing_components) {
        toast.error(`Missing components: ${error.response.data.missing_components.map(c => c.name).join(', ')}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading challenge...</div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Challenge not found</p>
          <button
            onClick={() => navigate('/challenges')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Challenge Hub
          </button>
        </div>
      </div>
    );
  }

  if (!isUnlocked) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/challenges')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Challenge Hub
          </button>

          <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-8 text-center">
            <Lock className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Challenge Locked</h2>
            <p className="text-slate-400 mb-6">
              Complete the prerequisite challenges to unlock this quest.
            </p>
            <button
              onClick={() => navigate('/challenges')}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              View Challenge Hub
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Historical Submission Banner */}
        {viewingHistoricalSubmission && (
          <div className="mb-4 p-3 bg-amber-900/30 border border-amber-600/50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-300">
              <History className="w-5 h-5" />
              <span className="font-medium">Viewing historical submission</span>
              <span className="text-amber-400/70 text-sm">- This is a past submission, not your best design</span>
            </div>
            <button
              onClick={() => navigate(`/challenge/${id}`)}
              className="px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-lg transition-colors"
            >
              Load Best Design
            </button>
          </div>
        )}

        {/* Header */}
        <button
          onClick={() => navigate('/challenges')}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Challenge Hub
        </button>

        {/* Challenge Info Card */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white mb-2">{challenge.title}</h1>
              <p className="text-slate-400 mb-4">{challenge.description}</p>

              <div className="flex items-center gap-4 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-sm font-medium capitalize ${
                    DIFFICULTY_COLORS[challenge.difficulty]
                  }`}
                >
                  {challenge.difficulty}
                </span>

                <div className="flex items-center gap-1 text-slate-400">
                  <Trophy className="w-4 h-4" />
                  <span className="text-sm font-medium">{challenge.base_reward} DP Reward</span>
                </div>

                {challenge.estimated_time_minutes && (
                  <div className="flex items-center gap-1 text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{challenge.estimated_time_minutes} min</span>
                  </div>
                )}

                {userProgress?.is_completed && (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Completed</span>
                  </div>
                )}
              </div>
            </div>

            {/* User Progress */}
            {userProgress && (
              <div className="text-right bg-slate-700/50 rounded-lg p-4 ml-4">
                <div className="text-sm text-slate-400 mb-1">Your Best Score</div>
                <div className="text-3xl font-bold text-primary-400">
                  {userProgress.best_score || 0}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {userProgress.attempts || 0} attempts
                </div>
              </div>
            )}
          </div>

          {/* Story Text - Collapsible */}
          <CollapsibleChallengeSection
            title="Scenario"
            bgColor="bg-blue-50"
            borderColor="border-blue-500"
            defaultOpen={false}
          >
            <div className="prose prose-sm max-w-none text-gray-700">
              <ReactMarkdown
                components={{
                  h2: ({children}) => <h4 className="text-lg font-bold text-gray-800 mt-4 mb-2">{children}</h4>,
                  h3: ({children}) => <h5 className="text-base font-semibold text-gray-700 mt-3 mb-1">{children}</h5>,
                  p: ({children}) => <p className="mb-3 leading-relaxed">{children}</p>,
                  ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                  ol: ({children}) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                  li: ({children}) => <li className="text-gray-700">{children}</li>,
                  strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
                  code: ({children}) => <code className="bg-blue-100 px-1.5 py-0.5 rounded text-blue-800 text-sm">{children}</code>,
                }}
              >
                {challenge.story_text}
              </ReactMarkdown>
            </div>
          </CollapsibleChallengeSection>

          {/* Requirements - Collapsible */}
          <CollapsibleChallengeSection
            title="Requirements"
            icon={Zap}
            bgColor="bg-yellow-50"
            borderColor="border-yellow-500"
            defaultOpen={false}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {Object.entries(challenge.requirements).map(([key, value]) => (
                <div key={key}>
                  <span className="text-gray-600 capitalize">
                    {key.replace(/_/g, ' ')}:
                  </span>{' '}
                  <span className="font-semibold">
                    {typeof value === 'number' ? value.toLocaleString() : value}
                  </span>
                </div>
              ))}
            </div>
          </CollapsibleChallengeSection>

          {/* Tags */}
          {challenge.tags && challenge.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {challenge.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Last Submission Results */}
        {lastSubmission && (
          <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6 mb-6">
            {/* Header with Score */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Latest Submission Results</h2>
              <div className={`flex items-center gap-3 px-4 py-2 rounded-xl ${
                lastSubmission.passed
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                  : 'bg-gradient-to-r from-orange-500 to-red-500'
              } text-white`}>
                <div className="text-center">
                  <div className="text-3xl font-bold">{lastSubmission.final_score}</div>
                  <div className="text-xs opacity-80">/ 1000</div>
                </div>
                <div className="text-sm">
                  {lastSubmission.passed ? (
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-5 h-5" />
                      <span>Passed!</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Target className="w-5 h-5" />
                      <span>Keep trying!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Feedback Content */}
            <FeedbackRenderer feedback={lastSubmission.feedback} aiScore={lastSubmission.ai_score} />

            {lastSubmission.passed && (
              <div className="mt-4 bg-green-100 border border-green-300 rounded-lg p-4 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-green-800 font-semibold">
                  Congratulations! You earned {lastSubmission.points_earned} Design Points!
                </p>
              </div>
            )}
          </div>
        )}

        {/* Design Canvas */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Design Your Solution</h2>

          {submitting ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-slate-400">Evaluating your design...</p>
              </div>
            </div>
          ) : (
            <DesignCanvas
              challenge={challenge}
              onSubmit={handleSubmitDesign}
              initialDesign={savedDesign}
              referenceSolution={referenceSolution}
              solutionLocked={solutionLocked}
              solutionUnlockCost={solutionUnlockCost}
              onSolutionUnlocked={(solutionJson) => {
                setSolutionLocked(false);
                setReferenceSolution(solutionJson);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallengePage;
